import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Banner, Button } from '@acs/ui';
import { useI18n } from '../../i18n/index.js';
import { refreshVerification, sendVerification, signOutUser } from '../../shared/api/auth.js';
import { friendlyError } from '../../shared/api/errors.js';
import { PageLoader } from '../../shared/ui/PageLoader.js';
import { useAuth } from './AuthProvider.js';
import { AuthShell } from './AuthShell.js';
import { authGate, GATE_PATH } from './authGate.js';

/** How often the page quietly checks whether the link was clicked elsewhere,
 *  and for how long before it leaves that to the button. */
const POLL_MS = 5_000;
const POLL_FOR_MS = 10 * 60_000;

type Notice = { tone: 'success' | 'info' | 'danger'; text: string };

/**
 * "Check your inbox" — where a signed-in account with an unconfirmed email
 * waits (doc 88 D10). Guarded by `authGate` itself rather than `RequireAuth`,
 * which would send this very state back here in a loop.
 *
 * Confirming on another device or tab is noticed by a quiet re-check while the
 * page is visible; "I've confirmed" does the same on demand. Either way the
 * forced token refresh is what lets the rules see the confirmed address.
 */
export function VerifyEmailPage() {
  const { t, locale } = useI18n();
  const auth = useAuth();
  const gate = authGate(auth);
  const [cooldown, setCooldown] = useState(0);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busy, setBusy] = useState<'resend' | 'check' | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  useEffect(() => {
    if (gate !== 'unverified') return;
    const started = Date.now();
    const id = window.setInterval(() => {
      if (Date.now() - started > POLL_FOR_MS) {
        window.clearInterval(id);
        return;
      }
      if (document.visibilityState === 'visible') void refreshVerification().catch(() => undefined);
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [gate]);

  if (gate === 'loading') return <PageLoader />;
  if (gate !== 'unverified') return <Navigate to={GATE_PATH[gate]} replace />;

  const onResend = async () => {
    setBusy('resend');
    setNotice(null);
    try {
      const res = await sendVerification(locale);
      switch (res.status) {
        case 'sent':
          setNotice({ tone: 'success', text: t('verify.sent') });
          setCooldown(60);
          break;
        case 'cooldown':
          setCooldown(res.retryAfterSec ?? 60);
          break;
        case 'already_verified':
          await refreshVerification();
          break;
        case 'unavailable':
          setNotice({ tone: 'danger', text: t('verify.unavailable') });
          break;
      }
    } catch (e) {
      setNotice({ tone: 'danger', text: friendlyError(t, e) });
    } finally {
      setBusy(null);
    }
  };

  const onCheck = async () => {
    setBusy('check');
    setNotice(null);
    try {
      if (!(await refreshVerification())) setNotice({ tone: 'info', text: t('verify.notYet') });
    } catch (e) {
      setNotice({ tone: 'danger', text: friendlyError(t, e) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <AuthShell title={t('verify.title')} lede={t('verify.lede', { email: auth.user?.email ?? '' })}>
      {notice && (
        <div className="auth__banner">
          <Banner tone={notice.tone}>{notice.text}</Banner>
        </div>
      )}
      <div className="auth__fields">
        <Button size="lg" block loading={busy === 'check'} disabled={busy === 'resend'} onClick={() => void onCheck()}>
          {t('verify.done')}
        </Button>
        <Button
          size="lg"
          variant="secondary"
          block
          loading={busy === 'resend'}
          disabled={busy === 'check' || cooldown > 0}
          onClick={() => void onResend()}
        >
          {cooldown > 0 ? t('verify.resendIn', { seconds: cooldown }) : t('verify.resend')}
        </Button>
      </div>
      <div className="auth__links auth__links--center">
        <button type="button" className="auth__link" onClick={() => void signOutUser()}>
          {t('verify.signOut')}
        </button>
      </div>
    </AuthShell>
  );
}
